/**
 * section-advanced.tsx — Mục "Nâng cao" của màn nhân viên AI (Cần phát hành), mỗi cài đặt một dòng (settings-row.tsx)
 * → bấm mở hộp thoại chi tiết (setting-dialog.tsx):
 *   Đợi khách nhắn xong (giây) · Kiểm tra lại câu trả lời trước khi gửi · Chế độ nhanh · AI giống người thật ·
 *   Tự động ngừng trả lời · Trả lời tin AI chưa đọc được · Trả lời trò chuyện nhóm (Mọi tin / Khi được gọi) · Bộ nhớ AI ·
 *   Lấy thời gian hiện tại · Gợi ý câu hỏi liên quan · Quy trình bán hàng riêng (+ "Chép từ mẫu" = Mẫu quy trình của Thư mục dùng chung).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Brain, Clock, Copy, Hourglass, ImageOff, ListOrdered, MessagesSquare, OctagonPause, ShieldCheck, SlidersHorizontal, Users, Zap, Lightbulb } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAiLibrary } from '@/hooks/use-ai-khung'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChipGroup, TagInput } from '../train/train-ui'
import { SettingsRow } from '../train/settings-row'
import { SettingDialog } from '../train/setting-dialog'
import { HumanLikeDialog } from '../train/human-like-dialog'
import { AutoStopDialog } from '../train/auto-stop-dialog'
import { MAX_DEBOUNCE_S, TIMEZONES, type BotSettings, type GroupReplyMode } from '../train/bot-settings'
import { StaffPanel } from './staff-ui'
import type { SectionProps } from '../train/draft-form'

type DialogKey = 'debounce' | 'memory' | 'humanLike' | 'autoStop' | 'unsupported' | 'group' | 'time' | 'playbook' | null

export function AdvancedSection({ botId, form, set, setSettings, canEdit }: SectionProps) {
  const { t } = useTranslation('aiStaff')
  const s = form.settings
  const GROUP_MODES: Array<{ value: GroupReplyMode; label: string }> = [
    { value: 'all', label: t('advanced.group.modeAll') },
    { value: 'trigger', label: t('advanced.group.modeTrigger') },
  ]
  const [dlg, setDlg] = useState<DialogKey>(null)
  const { data: library } = useAiLibrary()
  const playbookTemplate = library?.templates.playbook?.trim() || ''
  const close = (o: boolean) => { if (!o) setDlg(null) }
  const save = <K extends keyof BotSettings>(key: K) => (v: BotSettings[K]) => setSettings((x) => ({ ...x, [key]: v }))

  return (
    <StaffPanel
      id="staff-nang-cao" title={t('advanced.title')} icon={<SlidersHorizontal className="h-4 w-4" />} scope="publish"
      description={t('advanced.description')}
    >
      <div className="divide-y rounded-lg border">
        <SettingsRow kind="dialog" icon={<Hourglass className="h-4 w-4" />} title={t('advanced.debounce.title')} label={s.debounceSeconds > 0 ? t('advanced.debounce.seconds', { count: s.debounceSeconds }) : t('advanced.debounce.immediate')} onOpen={() => setDlg('debounce')} disabled={!canEdit}
          info={t('advanced.debounce.info')} />
        <SettingsRow kind="switch" icon={<ShieldCheck className="h-4 w-4" />} title={t('advanced.verify.title')} checked={s.verifyBeforeSend} onChange={(v) => setSettings((x) => ({ ...x, verifyBeforeSend: v }))} disabled={!canEdit}
          info={t('advanced.verify.info')} />
        <SettingsRow kind="switch" icon={<Zap className="h-4 w-4" />} title={t('advanced.fast.title')} checked={s.fastMode} onChange={(v) => setSettings((x) => ({ ...x, fastMode: v }))} disabled={!canEdit}
          info={t('advanced.fast.info')} />
        <SettingsRow kind="status" icon={<MessagesSquare className="h-4 w-4" />} title={t('advanced.humanLike.title')} on={s.humanLike.enabled} onOpen={() => setDlg('humanLike')} disabled={!canEdit}
          info={t('advanced.humanLike.info')} />
        <SettingsRow kind="status" icon={<OctagonPause className="h-4 w-4" />} title={t('advanced.autoStop.title')} on={s.autoStop.enabled} onOpen={() => setDlg('autoStop')} disabled={!canEdit}
          info={t('advanced.autoStop.info')} />
        <SettingsRow kind="status" icon={<ImageOff className="h-4 w-4" />} title={t('advanced.unsupported.title')} on={s.unsupportedReply.enabled} onOpen={() => setDlg('unsupported')} disabled={!canEdit}
          info={t('advanced.unsupported.info')} />
        <SettingsRow kind="dialog" icon={<Users className="h-4 w-4" />} title={t('advanced.group.title')} onOpen={() => setDlg('group')} disabled={!canEdit}
          label={!s.groupReply.enabled ? t('advanced.group.none') : s.groupReply.mode === 'trigger' ? t('advanced.group.modeTrigger') : t('advanced.group.modeAll')}
          info={t('advanced.group.info')} />
        <SettingsRow kind="dialog" icon={<Brain className="h-4 w-4" />} title={t('advanced.memory.title')} onOpen={() => setDlg('memory')} disabled={!canEdit}
          label={s.memory.enabled ? (s.memory.rounds ? t('advanced.memory.rounds', { count: s.memory.rounds }) : t('advanced.memory.on')) : t('advanced.memory.off')}
          info={t('advanced.memory.info')} />
        <SettingsRow kind="status" icon={<Clock className="h-4 w-4" />} title={t('advanced.time.rowTitle')} on={s.currentTime.enabled} onOpen={() => setDlg('time')} disabled={!canEdit}
          info={t('advanced.time.info')} />
        <SettingsRow kind="switch" icon={<Lightbulb className="h-4 w-4" />} title={t('advanced.related.title')} checked={s.suggestRelated} onChange={(v) => setSettings((x) => ({ ...x, suggestRelated: v }))} disabled={!canEdit}
          info={t('advanced.related.info')} />
        <SettingsRow kind="dialog" icon={<ListOrdered className="h-4 w-4" />} title={t('advanced.playbook.title')} label={form.playbookPrompt.trim() ? t('advanced.playbook.written') : t('advanced.playbook.setup')} onOpen={() => setDlg('playbook')} disabled={!canEdit} tag="beta"
          info={t('advanced.playbook.info')} />
      </div>

      {/* ── Đợi khách nhắn xong ── */}
      <SettingDialog
        open={dlg === 'debounce'} onOpenChange={close} icon={<Hourglass className="h-4 w-4" />} title={t('advanced.debounce.title')} canEdit={canEdit}
        description={t('advanced.debounce.dialogDesc')}
        value={s.debounceSeconds} onSave={(v) => setSettings((x) => ({ ...x, debounceSeconds: v }))}
        validate={(v) => (!Number.isInteger(v) || v < 0 || v > MAX_DEBOUNCE_S ? t('advanced.debounce.invalid', { max: MAX_DEBOUNCE_S }) : null)}
      >
        {(v, upd) => (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/30 p-3 text-sm">
            <span>{t('advanced.debounce.wait')}</span>
            <Input type="number" min={0} max={MAX_DEBOUNCE_S} className="h-8 w-24" value={Number.isFinite(v) ? v : ''}
              onChange={(e) => upd(() => (e.target.value === '' ? Number.NaN : Number(e.target.value)))} disabled={!canEdit} aria-label={t('advanced.debounce.ariaLabel')} />
            <span>{t('advanced.debounce.afterLast')}</span>
            <span className="text-xs text-muted-foreground">{t('advanced.debounce.range', { max: MAX_DEBOUNCE_S })}</span>
          </div>
        )}
      </SettingDialog>

      {/* ── Bộ nhớ AI ── */}
      <SettingDialog
        open={dlg === 'memory'} onOpenChange={close} icon={<Brain className="h-4 w-4" />} title={t('advanced.memory.title')} canEdit={canEdit}
        description={t('advanced.memory.dialogDesc')}
        value={s.memory} onSave={save('memory')}
        enabledKey={{ get: (m) => m.enabled, set: (m, on) => ({ ...m, enabled: on }) }} enabledLabel={t('advanced.memory.enabledLabel')}
        validate={(m) => (m.rounds != null && (m.rounds < 1 || m.rounds > 50) ? t('advanced.memory.invalid') : null)}
      >
        {(m, upd) => (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/30 p-3 text-sm">
            <span>{t('advanced.memory.read')}</span>
            <Input type="number" min={1} max={50} className="h-8 w-20" value={m.rounds ?? ''} placeholder={t('advanced.memory.auto')}
              onChange={(e) => upd((d) => ({ ...d, rounds: e.target.value === '' ? null : Math.round(Number(e.target.value)) }))} disabled={!canEdit} />
            <span>{t('advanced.memory.latestRounds')}</span>
            <span className="text-xs text-muted-foreground">{t('advanced.memory.autoHint')}</span>
          </div>
        )}
      </SettingDialog>

      {/* ── AI giống người thật (hộp riêng theo mẫu — human-like-dialog.tsx) ── */}
      <HumanLikeDialog open={dlg === 'humanLike'} onOpenChange={close} value={s.humanLike} onSave={save('humanLike')} canEdit={canEdit} />

      {/* ── Tự động ngừng trả lời (hộp riêng: auto-stop-dialog.tsx) ── */}
      <AutoStopDialog open={dlg === 'autoStop'} onOpenChange={close} value={s.autoStop} onSave={save('autoStop')} botId={botId} attributes={s.collectAttributes} canEdit={canEdit} />

      {/* ── Tin AI chưa đọc được ── */}
      <SettingDialog
        open={dlg === 'unsupported'} onOpenChange={close} icon={<ImageOff className="h-4 w-4" />} title={t('advanced.unsupported.title')} canEdit={canEdit}
        description={t('advanced.unsupported.info')}
        value={s.unsupportedReply} onSave={save('unsupportedReply')}
        enabledKey={{ get: (u) => u.enabled, set: (u, on) => ({ ...u, enabled: on }) }} enabledLabel={t('advanced.unsupported.enabledLabel')}
        validate={(u) => (u.enabled && !u.text.trim() ? t('advanced.unsupported.invalid') : null)}
      >
        {(u, upd) => (
          <div className="space-y-1">
            <Label className="text-xs">{t('advanced.unsupported.reply')}</Label>
            <Input maxLength={500} value={u.text} onChange={(e) => upd((d) => ({ ...d, text: e.target.value }))}
              placeholder={t('advanced.unsupported.placeholder')} disabled={!canEdit} />
          </div>
        )}
      </SettingDialog>

      {/* ── Trò chuyện nhóm ── */}
      <SettingDialog
        open={dlg === 'group'} onOpenChange={close} icon={<Users className="h-4 w-4" />} title={t('advanced.group.title')} canEdit={canEdit}
        description={t('advanced.group.info')}
        value={s.groupReply} onSave={save('groupReply')}
        enabledKey={{ get: (g) => g.enabled, set: (g, on) => ({ ...g, enabled: on }) }} enabledLabel={t('advanced.group.enabledLabel')}
        validate={(g) => {
          if (!g.enabled) return null
          if (g.mode === 'trigger' && !g.onMention && !g.onKeywords) return t('advanced.group.needTrigger')
          if (g.mode === 'trigger' && g.onKeywords && g.keywords.length === 0) return t('advanced.group.needKeyword')
          return null
        }}
      >
        {(g, upd) => (
          <>
            <div className="space-y-1">
              <Label className="text-xs">{t('advanced.group.whenReply')}</Label>
              <ChipGroup value={g.mode} options={GROUP_MODES} onChange={(v) => upd((d) => ({ ...d, mode: v }))} disabled={!canEdit} />
            </div>
            {g.mode === 'trigger' && (
              <div className="space-y-2 rounded-md bg-muted/30 p-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={g.onMention} onChange={(e) => upd((d) => ({ ...d, onMention: e.target.checked }))} disabled={!canEdit} />
                  {t('advanced.group.onMention')} <span className="text-xs text-muted-foreground">{t('advanced.group.onMentionHint')}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={g.onKeywords} onChange={(e) => upd((d) => ({ ...d, onKeywords: e.target.checked }))} disabled={!canEdit} />
                  {t('advanced.group.onKeywords')}
                </label>
                {g.onKeywords && <TagInput value={g.keywords} onChange={(v) => upd((d) => ({ ...d, keywords: v }))} placeholder={t('advanced.group.keywordsPlaceholder')} disabled={!canEdit} />}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">{t('advanced.group.ignore')} <span className="font-normal text-muted-foreground">{t('advanced.group.ignoreHint')}</span></Label>
              <TagInput value={g.ignoreMemberNames} onChange={(v) => upd((d) => ({ ...d, ignoreMemberNames: v }))} placeholder={t('advanced.group.ignorePlaceholder')} disabled={!canEdit} max={50} />
            </div>
          </>
        )}
      </SettingDialog>

      {/* ── Giờ hiện tại ── */}
      <SettingDialog
        open={dlg === 'time'} onOpenChange={close} icon={<Clock className="h-4 w-4" />} title={t('advanced.time.title')} canEdit={canEdit}
        description={t('advanced.time.description')}
        value={s.currentTime} onSave={save('currentTime')}
        enabledKey={{ get: (c) => c.enabled, set: (c, on) => ({ ...c, enabled: on }) }} enabledLabel={t('advanced.time.enabledLabel')}
      >
        {(c, upd) => (
          <div className="space-y-1">
            <Label className="text-xs">{t('advanced.time.timezone')}</Label>
            <Select value={c.timezone} onValueChange={(v) => upd((d) => ({ ...d, timezone: v }))} disabled={!canEdit}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((z) => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </SettingDialog>

      {/* ── Quy trình bán hàng riêng (playbook) ── */}
      <SettingDialog
        open={dlg === 'playbook'} onOpenChange={close} icon={<ListOrdered className="h-4 w-4" />} title={t('advanced.playbook.title')} canEdit={canEdit} size="lg"
        description={t('advanced.playbook.info')}
        value={form.playbookPrompt} onSave={(v) => set({ playbookPrompt: v })}
      >
        {(v, upd) => (
          <>
          {canEdit && <div className="flex justify-end">
            <Button type="button" size="sm" variant="outline" disabled={!playbookTemplate}
              title={playbookTemplate ? t('advanced.playbook.copyTitle') : t('advanced.playbook.copyTitleEmpty')}
              onClick={() => {
                if (v.trim() && !window.confirm(t('advanced.playbook.confirmReplace'))) return
                upd(() => playbookTemplate)
                toast.success(t('advanced.playbook.copied'))
              }}>
              <Copy className="h-3.5 w-3.5" /> {t('advanced.playbook.copyFromTemplate')}
            </Button>
          </div>}
          <Textarea rows={12} className="font-mono text-xs" value={v} onChange={(e) => upd(() => e.target.value)} disabled={!canEdit}
            placeholder={t('advanced.playbook.placeholder')} />
          </>
        )}
      </SettingDialog>
    </StaffPanel>
  )
}
